<template>
  <FullCalendar
    ref="fullCalendar"
    class="m-4"
    defaultView="timeGridWeek"
    :contentHeight="780"
    :customButtons="customButtons"
    :events="events"
    :eventTimeFormat="eventTimeFormat"
    :header="header"
    :locale="locale"
    :plugins="calendarPlugins"
    @eventClick="onEventClick"
    @dateClick="select"
  />
</template>

<script>
import moment from 'moment';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { mapState } from 'vuex';
import { Storage } from '@/enums/storage.enum';
import { capitalize } from '@/utils/string';
import { toClock } from '@/utils/time';

// @vuese
// Kalendarz
// @group Components
export default {
  name: 'Calendar',
  components: { FullCalendar },
  props: {
    // Lista wydarzeń
    events: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      calendarPlugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      customButtons: {
        month: this.createViewButton('buttons.month', 'month'),
        week: this.createViewButton('buttons.week', 'week'),
        day: this.createViewButton('buttons.day', 'day'),
        prev: this.createStepButton('fc-icon-chevron-left', -1),
        next: this.createStepButton('fc-icon-chevron-right', 1)
      },
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: false
      },
      header: {
        left: 'month,week,day',
        center: 'title',
        right: 'today prev,next'
      },
      locale: undefined
    };
  },
  methods: {
    // @vuese
    // Ustawia widok z params i datę z query
    init() {
      const param = capitalize(this.$route.params.view);
      const views = ['dayGridMonth', 'timeGridWeek', 'timeGridDay'];
      const view = views.find(item => item.includes(param));

      this.$refs.fullCalendar.getApi().changeView(view);
      this.$refs.fullCalendar.getApi().gotoDate(this.selectedDate);
    },
    // @vuese
    // Ustawia parametry i przeładowuje stronę przy zmianie widoku kalendarza
    // @arg `view` - nazwa widoku kalendarza: 'month','week','day'
    viewChange(view) {
      const nextRoute = {
        name: this.$route.name,
        params: { view },
        query: this.$route.query
      };

      this.$router.replace(nextRoute);
    },
    // @vuese
    // Przechodzi jeden krok do przodu lub do tyłu w kalendarzu
    // @arg `quantity` - ilość kroków
    stepChange(quantity) {
      const nextDate = moment(this.$refs.fullCalendar.getApi().view.currentStart).add(
        quantity,
        this.$route.params.view
      );

      const year = nextDate.year();
      const month = nextDate.month() + 1;
      const day = nextDate.date();
      const nextRoute = {
        name: this.$route.name,
        params: this.$route.params,
        query: { ...this.$route.query, year, month, day }
      };

      this.$router.replace(nextRoute);
    },
    // @vuese
    // Emit po kliknięciu na wydarzenie
    // @arg `event` - obiekt klikniętego wydarzenia
    onEventClick(event) {
      // Emit po kliknięciu na wydarzenie
      // @arg Obiekt klikniętego wydarzenia
      this.$emit('eventClick', event.event.extendedProps);
    },
    // @vuese
    // Emit po wybraniu daty
    select(event) {
      this.$emit('select', event);
    },
    // @vuese
    // Tworzy przyciski widoku kalendarza
    createViewButton(text, param) {
      return { text: this.$t(text), click: () => this.viewChange(param) };
    },
    // @vuese
    // Tworzy przyciski przechodzenia między widokami kalendarza
    createStepButton(icon, param) {
      return { icon, click: () => this.stepChange(param) };
    },
    importLocale() {
      if (this.lang === 'en') {
        this.locale = 'en';
        return Promise.resolve();
      }
      return import(/* webpackChunkName: "fc-[request]" */ `@fullcalendar/core/locales/${this.lang}`).then(
        localeObject => (this.locale = localeObject)
      );
    },
    customButtonsLang() {
      this.customButtons.month = this.createViewButton('buttons.month', 'month');
      this.customButtons.week = this.createViewButton('buttons.week', 'week');
      this.customButtons.day = this.createViewButton('buttons.day', 'day');
    }
  },
  computed: {
    selectedDate() {
      if (this.$route.query.year && this.$route.query.month && this.$route.query.day) {
        return `${this.$route.query.year}-${toClock(this.$route.query.month)}-${toClock(this.$route.query.day)}`;
      }
    },
    ...mapState({
      lang: state => state.system.lang
    })
  },
  watch: {
    $route() {
      this.init();
    },
    lang() {
      this.importLocale();
    },
    locale() {
      this.customButtonsLang();
    }
  },
  mounted() {
    this.importLocale()
      .then(() => this.init())
      .then(() => this.customButtonsLang());
  }
};
</script>
